import { useEffect, useState, useRef } from 'react';
import eliminarPuntosDuplicados from '../utils/eliminarPuntosDuplicados';
import getZoomForArea from '../utils/getZoomForArea';
import { buildPolylineIcons } from './useGeoPuntosLayer.jsx';

const buildPuntoColorIcon = (maps, fillColor) => ({
  path: maps.SymbolPath.CIRCLE,
  scale: 9,
  fillColor,
  fillOpacity: 1,
  strokeColor: '#fff',
  strokeWeight: 2,
});

const useCroquisPuntosHook = (
  parcelaData,
  existingPuntos,
  tipoGeometria,
  initialGeometry = null,
  initialZoom = 16,
  color = null,
  dashType = 'solid',
  previewText = '',
  previewPointColor = null
) => {
  const [zoom, setZoom] = useState(initialZoom);
  const [center, setCenter] = useState(() =>
    parcelaData?.centroid?.lat != null && parcelaData?.centroid?.lng != null ? parcelaData.centroid : { lat: 16, lng: -92 }
  );
  const [map, setMap] = useState(null);
  const [maps, setMaps] = useState(null);
  const [drawnGeometry, setDrawnGeometry] = useState(null);

  const drawnOverlayRef = useRef(null);
  const textOverlayRef = useRef(null);
  const clickListenersRef = useRef([]);
  const dblclickListenerRef = useRef(null);
  const suppressNextClickRef = useRef(false);

  const colorRef = useRef(color);
  const dashTypeRef = useRef(dashType);
  useEffect(() => {
    colorRef.current = color;
  }, [color]);
  useEffect(() => {
    dashTypeRef.current = dashType;
  }, [dashType]);
  const previewTextRef = useRef(previewText);
  useEffect(() => {
    previewTextRef.current = previewText;
  }, [previewText]);
  const previewPointColorRef = useRef(previewPointColor);
  useEffect(() => {
    previewPointColorRef.current = previewPointColor;
  }, [previewPointColor]);

  // Render existing parcela polygon (read-only)
  useEffect(() => {
    if (!map || !maps || !maps.geometry || !parcelaData?.parcelaPolygon) return;
    try {
      const poligono = JSON.parse(parcelaData.parcelaPolygon);
      let poligono_data = poligono.coordinates[0].map(p => ({ lat: p[1], lng: p[0] }));
      poligono_data = eliminarPuntosDuplicados(poligono_data);

      new maps.Polygon({
        paths: poligono_data,
        strokeColor: '#91B833',
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: '#f2f1e8',
        fillOpacity: 0.5,
        editable: false,
        draggable: false,
        clickable: false,
        map,
      });

      if (
        poligono &&
        poligono.coordinates &&
        poligono.coordinates[0] &&
        poligono.coordinates[0][0] &&
        poligono.coordinates[0][0][1] &&
        poligono.coordinates[0][0][0]
      ) {
        setCenter({ lat: poligono.coordinates[0][0][1], lng: poligono.coordinates[0][0][0] });
      } else if (parcelaData.centroid?.lat && parcelaData.centroid?.lng) {
        setCenter(parcelaData.centroid);
      }

      const area = maps.geometry.spherical.computeArea(poligono_data) / 10000;
      setZoom(getZoomForArea(area));
    } catch (e) {
      // ignore parse errors
    }
  }, [map, maps, parcelaData?.parcelaPolygon]);

  useEffect(() => {
    if (!map || parcelaData?.parcelaPolygon) return;
    if (parcelaData?.centroid?.lat != null && parcelaData?.centroid?.lng != null) {
      setCenter(parcelaData.centroid);
    }
  }, [map, parcelaData?.parcelaPolygon, parcelaData?.centroid]);

  useEffect(() => {
    if (!map || !maps || !existingPuntos?.length) return;
    existingPuntos.forEach(punto => {
      if (punto.tipo === 'punto' && punto.lat != null) {
        const marker = new maps.Marker({
          position: { lat: parseFloat(punto.lat), lng: parseFloat(punto.lng) },
          map,
          title: punto.nombre,
        });
        const iw = new maps.InfoWindow({ content: `<b>${punto.nombre}</b>` });
        marker.addListener('click', () => iw.open(map, marker));
      } else if (punto.tipo === 'linea' && Array.isArray(punto.coordinates)) {
        new maps.Polyline({
          path: punto.coordinates,
          strokeColor: punto.color || '#1565C0',
          strokeWeight: 3,
          map,
          clickable: false,
        });
      } else if ((punto.tipo === 'poligono' || punto.tipo === 'area') && Array.isArray(punto.coordinates)) {
        new maps.Polygon({
          paths: punto.coordinates,
          strokeColor: punto.color || '#E65100',
          strokeWeight: 2,
          fillColor: punto.color || '#E65100',
          fillOpacity: 0.2,
          map,
          clickable: false,
        });
      }
    });
  }, [map, maps, existingPuntos]);

  // Setup drawing mode via click listeners (no DrawingManager library needed)
  useEffect(() => {
    if (!map || !maps) return;

    // Clean up previous drawing state
    clickListenersRef.current.forEach(l => maps.event.removeListener(l));
    clickListenersRef.current = [];
    suppressNextClickRef.current = false;
    if (dblclickListenerRef.current) {
      maps.event.removeListener(dblclickListenerRef.current);
      dblclickListenerRef.current = null;
    }
    if (drawnOverlayRef.current) {
      drawnOverlayRef.current.setMap(null);
      drawnOverlayRef.current = null;
    }
    if (textOverlayRef.current) {
      textOverlayRef.current.setMap(null);
      textOverlayRef.current = null;
    }
    setDrawnGeometry(null);

    const createTextPreview = (position, onDrag) => {
      class TextPreviewOverlay extends maps.OverlayView {
        constructor() {
          super();
          this._div = null;
          this._bubble = null;
          this._position = position;
          this._text = previewTextRef.current;
          this._onMouseMove = null;
          this._onMouseUp = null;
        }
        onAdd() {
          const div = document.createElement('div');
          div.style.position = 'absolute';
          div.style.display = 'flex';
          div.style.flexDirection = 'column';
          div.style.alignItems = 'center';
          div.style.pointerEvents = 'none';
          div.style.transform = 'translate(-50%, -100%)';

          const bubble = document.createElement('div');
          bubble.style.backgroundColor = 'rgba(255,255,255,0.92)';
          bubble.style.border = '1px solid #c8d1a0';
          bubble.style.borderRadius = '6px';
          bubble.style.padding = '5px 10px';
          bubble.style.fontSize = '12px';
          bubble.style.color = '#333';
          bubble.style.boxShadow = '0 1px 4px rgba(0,0,0,0.18)';
          bubble.style.whiteSpace = 'nowrap';
          bubble.style.pointerEvents = 'auto';
          bubble.style.cursor = 'grab';
          bubble.title = 'Arrastra para mover';
          bubble.textContent = this._text;

          const tail = document.createElement('div');
          tail.style.width = '0';
          tail.style.height = '0';
          tail.style.borderLeft = '5px solid transparent';
          tail.style.borderRight = '5px solid transparent';
          tail.style.borderTop = '6px solid #c8d1a0';

          div.appendChild(bubble);
          div.appendChild(tail);
          this._div = div;
          this._bubble = bubble;
          this.getPanes().overlayMouseTarget.appendChild(div);

          bubble.addEventListener('mousedown', e => {
            e.preventDefault();
            e.stopPropagation();
            bubble.style.cursor = 'grabbing';
            const mapDiv = map.getDiv();
            const proj = this.getProjection();
            if (!proj) return;
            const rect = mapDiv.getBoundingClientRect();
            const anchorPx = proj.fromLatLngToContainerPixel(this._position);
            const grabOffsetX = e.clientX - rect.left - anchorPx.x;
            const grabOffsetY = e.clientY - rect.top - anchorPx.y;
            this._onMouseMove = moveEvent => {
              const latLng = proj.fromContainerPixelToLatLng(
                new maps.Point(moveEvent.clientX - rect.left - grabOffsetX, moveEvent.clientY - rect.top - grabOffsetY)
              );
              this.setPosition(latLng);
              if (onDrag) onDrag(latLng);
            };
            this._onMouseUp = () => {
              bubble.style.cursor = 'grab';
              suppressNextClickRef.current = true;
              document.removeEventListener('mousemove', this._onMouseMove);
              document.removeEventListener('mouseup', this._onMouseUp);
            };
            document.addEventListener('mousemove', this._onMouseMove);
            document.addEventListener('mouseup', this._onMouseUp);
          });
        }
        draw() {
          const proj = this.getProjection();
          if (!proj || !this._div) return;
          const px = proj.fromLatLngToDivPixel(this._position);
          this._div.style.left = `${px.x}px`;
          this._div.style.top = `${px.y - 10}px`;
        }
        onRemove() {
          if (this._onMouseMove) document.removeEventListener('mousemove', this._onMouseMove);
          if (this._onMouseUp) document.removeEventListener('mouseup', this._onMouseUp);
          if (this._div && this._div.parentNode) {
            this._div.parentNode.removeChild(this._div);
            this._div = null;
            this._bubble = null;
          }
        }
        setPosition(pos) {
          this._position = pos;
          this.draw();
        }
        setText(text) {
          this._text = text || '';
          if (this._bubble) this._bubble.textContent = this._text;
        }
      }
      const overlay = new TextPreviewOverlay();
      overlay.setMap(map);
      return overlay;
    };

    const TEXTO_MARKER_ICON = {
      path: maps.SymbolPath.CIRCLE,
      scale: 6,
      fillColor: '#4A6700',
      fillOpacity: 1,
      strokeColor: '#fff',
      strokeWeight: 2,
    };

    const getPuntoIcon = isTexto => {
      if (isTexto) return TEXTO_MARKER_ICON;
      return previewPointColorRef.current ? buildPuntoColorIcon(maps, previewPointColorRef.current) : undefined;
    };

    if (tipoGeometria === 'punto' || tipoGeometria === 'texto') {
      const isTexto = tipoGeometria === 'texto';
      if (initialGeometry) {
        // Edit mode: pre-place draggable marker; also allow click-to-reposition
        const marker = new maps.Marker({
          position: initialGeometry,
          map,
          draggable: true,
          icon: getPuntoIcon(isTexto),
        });
        drawnOverlayRef.current = marker;
        if (isTexto) {
          textOverlayRef.current = createTextPreview(initialGeometry, latLng => {
            marker.setPosition(latLng);
            setDrawnGeometry({ lat: latLng.lat(), lng: latLng.lng() });
          });
        }
        setDrawnGeometry({ lat: initialGeometry.lat, lng: initialGeometry.lng });

        if (isTexto) {
          marker.addListener('drag', () => {
            if (textOverlayRef.current) textOverlayRef.current.setPosition(marker.getPosition());
          });
        }
        marker.addListener('dragend', () => {
          const pos = marker.getPosition();
          setDrawnGeometry({ lat: pos.lat(), lng: pos.lng() });
        });
        const l = maps.event.addListener(map, 'click', e => {
          if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            return;
          }
          marker.setPosition(e.latLng);
          if (isTexto && textOverlayRef.current) textOverlayRef.current.setPosition(e.latLng);
          setDrawnGeometry({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        });
        clickListenersRef.current.push(l);
      } else {
        const l = maps.event.addListener(map, 'click', e => {
          if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            return;
          }
          if (drawnOverlayRef.current) drawnOverlayRef.current.setMap(null);
          if (textOverlayRef.current) textOverlayRef.current.setMap(null);
          const marker = new maps.Marker({
            position: e.latLng,
            map,
            draggable: true,
            icon: getPuntoIcon(isTexto),
          });
          drawnOverlayRef.current = marker;
          if (isTexto) {
            textOverlayRef.current = createTextPreview(e.latLng, latLng => {
              marker.setPosition(latLng);
              setDrawnGeometry({ lat: latLng.lat(), lng: latLng.lng() });
            });
          }
          setDrawnGeometry({ lat: e.latLng.lat(), lng: e.latLng.lng() });
          if (isTexto) {
            marker.addListener('drag', () => {
              if (textOverlayRef.current) textOverlayRef.current.setPosition(marker.getPosition());
            });
          }
          marker.addListener('dragend', () => {
            const pos = marker.getPosition();
            setDrawnGeometry({ lat: pos.lat(), lng: pos.lng() });
          });
        });
        clickListenersRef.current.push(l);
      }
    } else if (tipoGeometria === 'linea') {
      const initialPath = initialGeometry || [];
      const initialDash = dashTypeRef.current;
      const polyline = new maps.Polyline({
        path: initialPath,
        strokeColor: colorRef.current || '#1565C0',
        strokeWeight: 3,
        strokeOpacity: initialDash === 'solid' ? 1 : 0,
        icons: buildPolylineIcons(maps, initialDash),
        editable: true,
        map,
      });
      drawnOverlayRef.current = polyline;

      if (initialGeometry && initialGeometry.length >= 2) {
        setDrawnGeometry(initialGeometry);
      }

      const syncLine = () => {
        const pts = polyline
          .getPath()
          .getArray()
          .map(p => ({ lat: p.lat(), lng: p.lng() }));
        if (pts.length >= 2) setDrawnGeometry(pts);
        else setDrawnGeometry(null);
      };
      // Keep drawnGeometry in sync when vertices are dragged or inserted via editable handles
      maps.event.addListener(polyline.getPath(), 'set_at', syncLine);
      maps.event.addListener(polyline.getPath(), 'insert_at', syncLine);
      maps.event.addListener(polyline.getPath(), 'remove_at', syncLine);
      // Right-click a vertex to delete it (same as croquis editor)
      maps.event.addListener(polyline, 'rightclick', event => {
        if (event.vertex == null) return;
        polyline.getPath().removeAt(event.vertex);
      });

      if (!initialGeometry) {
        // Creation mode: click to add points, dblclick to finish
        let drawing = true;
        const cl = maps.event.addListener(map, 'click', e => {
          if (!drawing) return;
          polyline.getPath().push(e.latLng);
          const pts = polyline
            .getPath()
            .getArray()
            .map(p => ({ lat: p.lat(), lng: p.lng() }));
          if (pts.length >= 2) setDrawnGeometry(pts);
        });
        // dblclick fires click first — remove that duplicate point and stop adding new ones
        const dl = maps.event.addListener(map, 'dblclick', () => {
          if (!drawing) return;
          drawing = false;
          const path = polyline.getPath();
          if (path.getLength() > 0) path.removeAt(path.getLength() - 1);
          const pts = path.getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
          if (pts.length >= 2) setDrawnGeometry(pts);
        });
        clickListenersRef.current.push(cl);
        dblclickListenerRef.current = dl;
      }
    } else if (tipoGeometria === 'poligono' || tipoGeometria === 'area') {
      if (initialGeometry) {
        // Edit mode: pre-render editable polygon from existing coordinates
        const polygon = new maps.Polygon({
          paths: initialGeometry,
          strokeColor: colorRef.current || '#E65100',
          strokeWeight: 2,
          fillColor: colorRef.current || '#E65100',
          fillOpacity: 0.2,
          editable: true,
          map,
        });
        drawnOverlayRef.current = polygon;
        setDrawnGeometry(initialGeometry);
        const syncPoly = () => {
          const pts = polygon
            .getPath()
            .getArray()
            .map(p => ({ lat: p.lat(), lng: p.lng() }));
          if (pts.length >= 3) setDrawnGeometry(pts);
          else setDrawnGeometry(null);
        };
        maps.event.addListener(polygon.getPath(), 'set_at', syncPoly);
        maps.event.addListener(polygon.getPath(), 'insert_at', syncPoly);
        maps.event.addListener(polygon.getPath(), 'remove_at', syncPoly);
        maps.event.addListener(polygon, 'rightclick', event => {
          if (event.vertex == null) return;
          polygon.getPath().removeAt(event.vertex);
        });
      } else {
        // Creation mode — create polygon lazily on first click
        // (new Polygon({ paths: [] }) leaves getPath() undefined in some Maps API versions)
        let drawing = true;

        const syncPoly = () => {
          if (!drawnOverlayRef.current) return;
          const pts = drawnOverlayRef.current
            .getPath()
            .getArray()
            .map(p => ({ lat: p.lat(), lng: p.lng() }));
          if (pts.length >= 3) setDrawnGeometry(pts);
          else setDrawnGeometry(null);
        };

        const cl = maps.event.addListener(map, 'click', e => {
          if (!drawing) return;
          if (!drawnOverlayRef.current) {
            const polygon = new maps.Polygon({
              strokeColor: colorRef.current || '#E65100',
              strokeWeight: 2,
              fillColor: colorRef.current || '#E65100',
              fillOpacity: 0.2,
              editable: true,
              map,
            });
            drawnOverlayRef.current = polygon;
            // Keep drawnGeometry in sync when vertices are dragged or inserted
            maps.event.addListener(polygon.getPath(), 'set_at', syncPoly);
            maps.event.addListener(polygon.getPath(), 'insert_at', syncPoly);
            maps.event.addListener(polygon.getPath(), 'remove_at', syncPoly);
            // Right-click a vertex to delete it (same as croquis editor)
            maps.event.addListener(polygon, 'rightclick', event => {
              if (event.vertex == null) return;
              polygon.getPath().removeAt(event.vertex);
            });
          }
          drawnOverlayRef.current.getPath().push(e.latLng);
          const pts = drawnOverlayRef.current
            .getPath()
            .getArray()
            .map(p => ({ lat: p.lat(), lng: p.lng() }));
          if (pts.length >= 3) setDrawnGeometry(pts);
        });
        // dblclick fires click first — remove that duplicate point and stop adding new ones
        const dl = maps.event.addListener(map, 'dblclick', () => {
          if (!drawing) return;
          drawing = false;
          if (!drawnOverlayRef.current) return;
          const path = drawnOverlayRef.current.getPath();
          if (path.getLength() > 0) path.removeAt(path.getLength() - 1);
          const pts = path.getArray().map(p => ({ lat: p.lat(), lng: p.lng() }));
          if (pts.length >= 3) setDrawnGeometry(pts);
        });
        clickListenersRef.current.push(cl);
        dblclickListenerRef.current = dl;
      }
    }
  }, [map, maps, tipoGeometria, initialGeometry]);

  useEffect(() => {
    if (!maps || !drawnOverlayRef.current) return;
    if (tipoGeometria === 'linea') {
      drawnOverlayRef.current.setOptions({
        strokeColor: color || '#1565C0',
        strokeOpacity: dashType === 'solid' ? 1 : 0,
        icons: buildPolylineIcons(maps, dashType),
      });
    } else if (tipoGeometria === 'poligono' || tipoGeometria === 'area') {
      drawnOverlayRef.current.setOptions({
        strokeColor: color || '#E65100',
        fillColor: color || '#E65100',
      });
    }
  }, [color, dashType, tipoGeometria, maps]);

  useEffect(() => {
    if (!maps || tipoGeometria !== 'punto' || !drawnOverlayRef.current) return;
    drawnOverlayRef.current.setIcon(previewPointColor ? buildPuntoColorIcon(maps, previewPointColor) : null);
  }, [previewPointColor, tipoGeometria, maps]);

  useEffect(() => {
    if (tipoGeometria !== 'texto' || !textOverlayRef.current) return;
    textOverlayRef.current.setText(previewText);
  }, [previewText, tipoGeometria]);

  const renderMap = (mapInst, mapsInst) => {
    setMap(mapInst);
    setMaps(mapsInst);
  };

  return { renderMap, center, zoom, drawnGeometry, map, maps };
};

export default useCroquisPuntosHook;
